import { EU27 } from '../pages/CreateShipment/constants';
import { convertAmountToEur } from './currencyConversion';
import { userLogin, hasAdminAccess } from '../services/hasAdminAccess';

/**
 * Determines whether EU and US CPSC compliance fields are required.
 * When a user is logged in (userLogin() === true or hasAdminAccess() === true),
 * compliance fields are optional (not required).
 * For non-logged-in / guest users, compliance fields remain required based on destination rules.
 */
export function isComplianceRequired(explicitIsUserLogin?: boolean): boolean {
  if (typeof explicitIsUserLogin === 'boolean') {
    return !explicitIsUserLogin;
  }
  try {
    const isPortalUser = userLogin();
    const isAdmin = hasAdminAccess();
    return !(isPortalUser || isAdmin);
  } catch (err) {
    return true;
  }
}

/**
 * Checks if a country name matches any EU-27 member state (case-insensitive).
 */
export function isEUCountry(countryName?: string | null): boolean {
  if (!countryName || typeof countryName !== 'string') return false;
  const clean = countryName.trim().toLowerCase();
  return EU27.some(eu => eu.toLowerCase() === clean);
}

/**
 * Resolves the total shipment value following the priority order:
 * 1. Direct shipment value from "Shipment Value & Weight" section (if available and > 0)
 * 2. Fallback: Computed sum of (quantity * unit_value) across all boxes and products
 */
export function resolveShipmentValue(boxes: any[] = [], directShipmentValue?: number | null): number {
  if (
    directShipmentValue !== undefined &&
    directShipmentValue !== null &&
    !isNaN(directShipmentValue) &&
    directShipmentValue > 0
  ) {
    return Number(directShipmentValue);
  }

  let computedSum = 0;
  if (Array.isArray(boxes)) {
    boxes.forEach(box => {
      if (box && Array.isArray(box.products)) {
        box.products.forEach((p: any) => {
          const qty = Number(p.quantity ?? p.qty ?? 0);
          const val = Number(p.unitValue ?? p.value ?? 0);
          if (!isNaN(qty) && !isNaN(val) && qty > 0 && val > 0) {
            computedSum += qty * val;
          }
        });
      }
    });
  }

  return Number(computedSum.toFixed(2));
}

/**
 * Resolves total shipment value and converts it into EUR using the conversion rate helper.
 * Optionally accepts a live/custom rate (e.g. from useEurExchangeRate hook).
 */
export function resolveShipmentValueInEur(
  boxes: any[] = [],
  currency = 'EUR',
  directShipmentValue?: number | null,
  customRate?: number | null,
): number {
  const value = resolveShipmentValue(boxes, directShipmentValue);
  return convertAmountToEur(value, currency, customRate);
}

/**
 * Evaluates EU compliance rules for Product Identification & EU Compliance fields:
 * - EU + B2C + value <= €150: Visible and REQUIRED (Merchant ID & Manufacturer part no.)
 * - EU + B2B: Visible and OPTIONAL (none required)
 * - Otherwise: Hidden
 */
export function getEuComplianceRule(
  arg1?:
    | {
        destCountry?: string | null;
        shipmentMode?: string | null;
        shipmentValueEur: number;
        isUserLogin?: boolean;
      }
    | string
    | null,
  arg2?: string | null,
  arg3?: number,
  arg4?: boolean,
) {
  let destCountry: string | null | undefined;
  let shipmentMode: string | null | undefined;
  let shipmentValueEur = 0;
  let explicitIsUserLogin: boolean | undefined = undefined;

  if (typeof arg1 === 'object' && arg1 !== null) {
    destCountry = arg1.destCountry;
    shipmentMode = arg1.shipmentMode;
    shipmentValueEur = arg1.shipmentValueEur || 0;
    explicitIsUserLogin = arg1.isUserLogin;
  } else {
    destCountry = arg1;
    shipmentMode = arg2;
    shipmentValueEur = arg3 || 0;
    explicitIsUserLogin = arg4;
  }

  const isEU = isEUCountry(destCountry);
  const mode = (shipmentMode || '').trim().toLowerCase();
  const isB2C = mode === 'b2c';
  const isB2B = mode === 'b2b';
  const isUnderOrEqual150 = shipmentValueEur <= 150;

  // EU Compliance fields (M-PID, Barcode S-PID, NS-PID) are visible for EU destination in both B2C and B2B
  const isVisible = isEU && (isB2C || isB2B);

  // Required only for EU + B2C + goods total <= €150, when not bypassed by isUserLogin
  const complianceRequired = isComplianceRequired(explicitIsUserLogin);
  const isRequired = complianceRequired && isEU && isB2C && isUnderOrEqual150;

  return {
    isEU,
    isB2C,
    isB2B,
    isUnderOrEqual150,
    isVisible,
    isRequired,
    showEuComplianceFields: isVisible,
    requireEuComplianceFields: isRequired,
  };
}
